import { OfferTransportKind } from '@/common/enums/OfferTransportKind';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { DataBagService } from 'src/app/common/data-bag.service';
import { DateService } from 'src/app/common/date.service';
import { FirstDayDateAdapter } from 'src/app/common/first-day-date-adapter';
import { InfoboxService } from 'src/app/common/infobox/infobox.service';
import { LoaderService } from 'src/app/common/loader/loader.service';
import { GroupList, GroupOfferTerms, GroupProduct, ProductList } from '../group-offer.model';

@Component({
	selector: 'app-group-offer-terms',
	templateUrl: './group-offer-terms.component.html',
	styleUrls: ['./group-offer-terms.component.css'],
	providers: [
		{ provide: MAT_DATE_LOCALE, useValue: 'pl-PL' },
		{ provide: DateAdapter, useClass: FirstDayDateAdapter },
	],
})
export class GroupOfferTermsComponent implements OnChanges {
	@Input() editable: boolean;
	@Input() groupProducts: Array<GroupProduct>;
	@Input() data: GroupList;
	@Input() products: ProductList[];
	@Output() afterGenerate = new EventEmitter<void>();

	nightsStay: number = null;
	termsCreateType = null;
	termsCreateLogic = 'from-to';
	termsCreateRange: { start: Date; end: Date } = {
		start: null,
		end: null,
	};
	selected = null;
	termsToGenerate: GroupOfferTerms[] = [];
	productsToChange: GroupProduct[] = [];
	weekDay = 1;
	weekDiff = 1;
	importText = '';
	productPriceSettings = [];
	offerTermTransport = OfferTransportKind.COACH;

	readonly OfferTermTransport = OfferTransportKind;

	constructor(
		protected dateService: DateService,
		private readonly bag: DataBagService,
		private readonly loader: LoaderService,
		private readonly infobox: InfoboxService,
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['groupProducts']) {
			this.productsToChange = this.groupProducts;
			this.groupProducts.forEach((element) => {
				this.bag.get(
					'/offer/term/?limit=9999&product=' + element.product + '&group_offer=' + this.data.id,
					(result) => {
						element['termCount'] = result.results?.length;
						const product = this.products.find((el) => el.id == element.product);
						element['nightStay'] =
							typeof product.nightsStay === 'number'
								? product.nightsStay + (product.isNightEvent ? 1 : 0)
								: null;
						element['isNightEvent'] = product.isNightEvent;
					},
				);
			});
		}
	}

	resetTerms(): void {
		this.termsCreateRange = {
			start: null,
			end: null,
		};
	}

	updateTermsCalendar(selectedDate: Date): void {
		const lower = this.dateService.daysAdd(0, selectedDate);
		const upper = this.dateService.daysAdd(this.nightsStay, selectedDate);
		this.updateTerms(lower, upper);
		this.selected = null;
	}

	updateTermsDateRange(): void {
		if (this.termsCreateRange.start && this.termsCreateRange.end) {
			if (this.termsCreateLogic == 'from-to') {
				this.updateTermsFromTo();
				return;
			}
			this.updateTermsWeekDays();
		}
	}
	updateTermsFromTo(): void {
		const dayIterator = this.nightsStay != 0 ? this.nightsStay : 1;
		for (
			let lower = new Date(this.termsCreateRange.start);
			lower <= this.termsCreateRange.end;
			lower.setDate(lower.getDate() + dayIterator)
		) {
			const upper = new Date(lower.valueOf());
			upper.setDate(lower.getDate() + this.nightsStay);
			if (upper <= this.termsCreateRange.end) {
				this.updateTerms(
					this.dateService.datetimeWithTimezone(lower).date,
					this.dateService.datetimeWithTimezone(upper).date,
				);
			}
		}
	}

	updateTermsWeekDays(): void {
		let mod = -1;
		for (
			let lower = new Date(this.termsCreateRange.start);
			lower <= this.termsCreateRange.end;
			lower.setDate(lower.getDate() + 1)
		) {
			if (lower.getDay() == this.weekDay) {
				mod++;
				if (mod % this.weekDiff == 0) {
					const upper = new Date(lower.valueOf());
					upper.setDate(lower.getDate() + this.nightsStay);
					if (upper <= this.termsCreateRange.end) {
						this.updateTerms(
							this.dateService.datetimeWithTimezone(lower).date,
							this.dateService.datetimeWithTimezone(upper).date,
						);
					}
				}
			}
		}
	}

	generateTerms(): void {
		this.loader.show();
		const dataToSend = [];

		this.productsToChange.forEach((product) => {
			this.termsToGenerate.forEach((term) => {
				dataToSend.push({
					group_product: product.id,
					date_range: term.dateRange,
					price_priority: 'T',
					stopSale: this.data.stopSale,
					sale: this.data.sale,
					www: this.data.www,
					merlinx: this.data.merlinx,
					promotion: this.data.promotion,
					transportKind: term.offerTerm,
				});
			});
		});
		this.bag.post('/offer/term/', dataToSend, (result, error) => {
			if (error) {
				this.loader.hide();
				return;
			}
			this.termsToGenerate = [];
			this.resetTerms();
			this.loader.hide();
			this.nightsStay = null;
			this.infobox.success('Zapisano');
			this.afterGenerate.emit();
		});
	}

	getProductName(id: number): ProductList['name'] {
		return this.products.find((obj) => obj.id == id)['name'];
	}

	clearTerm(item): void {
		this.termsToGenerate = this.termsToGenerate.filter((s) => s != item);
	}

	clearAllTerms(): void {
		this.termsToGenerate = [];
	}

	importTerms(): void {
		const importedArray = this.importText.split(/[\t\n\s]/);
		importedArray.forEach((element) => {
			if (!element) {
				return;
			}
			const lower = this.dateService.daysAdd(0, element);
			const upper = this.dateService.daysAdd(this.nightsStay, element);
			this.updateTerms(lower, upper);
		});
	}

	checkNightsStay(): void {
		let errors = '';
		this.productsToChange.forEach((element) => {
			if (this.nightsStay != element.nightStay && element.nightStay != null) {
				errors +=
					this.products.find((el) => el.id == element.product)['name'] +
					' - Ilość noclegów: ' +
					element.nightStay +
					'<br />';
			}
		});

		if (errors && this.offerTermTransport !== OfferTransportKind.PLANE) {
			this.infobox.error(
				'Wprowadzona liczba noclegów nie zgadza się z ilością noclegów w poniższych produktach: <br><br> ' +
					errors,
			);
			this.nightsStay = null;
		}
	}

	nightsAutocomplete(): void {
		const firstInt = this.productsToChange.find((el) => el.nightStay !== null);
		if (!firstInt) {
			return;
		}
		let change = true;
		this.productsToChange.forEach((element) => {
			if (element.nightStay != firstInt.nightStay && element.nightStay != null) {
				change = false;
			}
		});
		if (change) {
			this.nightsStay = firstInt.nightStay;
		}
	}

	private pushTerms(
		index: number,
		dateRange: GroupOfferTerms['dateRange'],
		offerTerm: OfferTransportKind,
		offerTermToCancel: OfferTransportKind,
	): void {
		if (this.offerTermTransport !== offerTermToCancel && index === -1) {
			this.termsToGenerate.push({
				dateRange,
				offerTerm,
			});
		}
	}

	private findTermIndex(
		lower: GroupOfferTerms['dateRange']['lower'],
		upper: GroupOfferTerms['dateRange']['upper'],
		offerTerm: OfferTransportKind,
	): number {
		return this.termsToGenerate.findIndex(
			(element) =>
				element.dateRange.lower === lower &&
				element.dateRange.upper === upper &&
				element.offerTerm === offerTerm,
		);
	}

	private updateTerms(lower: string, upper: string): void {
		const indexCoach = this.findTermIndex(lower, upper, OfferTransportKind.COACH);
		const indexPlane = this.findTermIndex(lower, upper, OfferTransportKind.PLANE);

		if (indexCoach !== -1 && indexPlane !== -1) {
			return;
		}

		const dateRange = {
			lower: lower,
			upper: upper,
			bounds: '[]',
		};

		this.pushTerms(indexCoach, dateRange, OfferTransportKind.COACH, OfferTransportKind.PLANE);
		this.pushTerms(indexPlane, dateRange, OfferTransportKind.PLANE, OfferTransportKind.COACH);
	}
}
